package com.vamvisions.main.core.presentation.i18n

import com.vamvisions.main.core.domain.preferences.Language

/**
 * Every text the website interface shows. Each language implements it, so a missing translation is
 * a compile error.
 */
interface Strings {
    // Navigation
    val navHome: String
    val navPortfolio: String
    val navProjects: String
    val navContact: String
    val mainNavigationLabel: String
    val skipToContent: String

    // Toggles
    val switchToDarkScheme: String
    val switchToLightScheme: String
    val changeLanguage: String

    // Page titles
    val homeTitle: String
    val portfolioTitle: String
    val projectsTitle: String
    val contactTitle: String
    val notFoundTitle: String

    // Not-found page
    val notFoundMessage: String
    val backToHome: String

    // Errors
    val errorNoConnection: String
    val errorNotFound: String
    val errorServer: String
    val errorInvalidResponse: String
    val errorUnknown: String
    val errorBadRequest: String
    val errorTooManyRequests: String

    // Loading and retry
    val loading: String
    val retry: String

    // Home page
    val introTitle: String
    val introSubtitle: String

    /** The unit after the animated "<years>+" on the first highlight card, such as "years". */
    val highlightExperienceUnit: String

    /** The whole first highlight title, such as "8+ years", read by assistive technology instead of the animation. */
    fun highlightExperienceTitle(years: Int): String
    val highlightExperienceAccent: String
    val highlightExperienceText: String
    val highlightTechnicalTitle: String
    val highlightTechnicalAccent: String
    val highlightTechnicalText: String
    val highlightBusinessTitle: String
    val highlightBusinessAccent: String
    val highlightBusinessText: String

    // "Let's connect" card, on the Home and Portfolio pages
    val connectTitle: String
    val connectText: String
    val connectLink: String

    // Portfolio page
    val portfolioIntroTitle: String
    val portfolioIntroText: String
    val portfolioExperienceEyebrow: String
    val portfolioExperienceTitle: String
    val portfolioLearningEyebrow: String
    val portfolioLearningTitle: String
    val portfolioEducationEyebrow: String
    val portfolioEducationTitle: String
    val portfolioSeeProjects: String
    val portfolioWorkWithMe: String

    // Contact page
    val contactIntroTitle: String
    val contactIntroText: String

    /** The text alternative of the owner's photo. */
    val contactPhotoAlt: String

    /** The heading of the card with the email, GitHub and Instagram link pills. */
    val contactReachTitle: String
    val contactFormTitle: String

    /** The topic chips' question, followed by [contactOptional]. */
    val contactTopicLabel: String
    val contactOptional: String
    val contactTopicAppDevelopment: String
    val contactTopicDigitalProduct: String
    val contactTopicPartnership: String
    val contactTopicOther: String
    val contactNameLabel: String
    val contactNamePlaceholder: String
    val contactEmailLabel: String
    val contactEmailPlaceholder: String
    val contactMessageLabel: String
    val contactMessagePlaceholder: String
    val contactSend: String
    val contactSending: String
    val contactSent: String

    /** Any send failure except the rate limit. */
    val contactSendFailed: String
    val errorFieldRequired: String
    val errorEmailInvalid: String
    val errorMessageRequired: String
    val errorMessageTooShort: String
    val errorMessageTooLong: String
    val errorNameTooLong: String

    // Projects page
    val projectsIntroTitle: String
    val projectsIntroText: String
    val projectsNowEyebrow: String
    val projectsNowTitle: String
    val projectsEarlierEyebrow: String
    val projectsEarlierTitle: String

    /** The heading of the call-to-action card that ends the Projects page. Its link is [connectLink]. */
    val projectsConnectTitle: String
    val projectsConnectText: String

    /** The badge of a project that is not out yet. CSS sets it in capitals. */
    val comingSoon: String
    val visitSite: String
    val privacyPolicy: String
    val sourceCode: String

    // Document page
    /** Accessible name of the scrollable region around a document table. */
    val documentTable: String

    // Periods
    /** The end of an ongoing period, such as "Jun 2022 – now". */
    val present: String

    /** The 12 short month names, January first, as they appear inside a period. */
    val shortMonthNames: List<String>

    // Mobile menu
    val openMenu: String
    val closeMenu: String
}

/** The short label for [language] shown on the language switch. It is the same in every language. */
fun languageLabel(language: Language): String = language.code.uppercase()

fun stringsFor(language: Language): Strings = when (language) {
    Language.EN -> EnStrings
    Language.FR -> FrStrings
    Language.RO -> RoStrings
}
